import { useEffect, useMemo, useRef, useState } from 'react';
import Modal from '../../components/Modal.tsx';
import { deckSlugFor, sanitiseDeckName } from '../../lib/format.ts';
import { readJson } from '../../lib/api.ts';
import { Minus, Paperclip, Plus, Trash, X } from '../../lib/icons.tsx';
import AccentSwatches from '../../canvas/AccentSwatches.tsx';
import { OptionRow, OptionRows, Segmented, Toggle } from '../../components/Controls.tsx';
import ImageNotes, {
  noteKey,
  serialiseImageNotes,
  sourceKey,
  type ImageNote,
  type NoteItem,
  type SerialisedImageNote,
} from './ImageNotes.tsx';
import {
  AUDIENCE_CHIPS,
  VERBATIM_CHIPS,
  BUILD_MODEL,
  DOC_RE,
  IMG_RE,
  isSidecarDoc,
  KEY_RE,
  LEGACY_PPT_RE,
  KEYNOTE_UPLOAD_NOTICE,
  LEGACY_PPT_UPLOAD_NOTICE,
  deriveTitle,
  budgetForCount,
  recommendVerbatim,
  estimateSlides,
  detectSourceDeck,
  useDeckNameCheck,
  listNames,
  sourceStatus,
} from './shared.tsx';
import type { SourceDetail, Verbatim } from './shared.tsx';
import { mediaKey } from '../../../shared/media-keys.ts';
import type { EngineChoiceBlock, IngestSummary, PiProfile } from '../../../shared/api.ts';

/** Where a saved Pi setup points, in the words the Build engine row above uses
 *  for the LIVE setting — the two sentences sit one under the other, and a row
 *  that described the same fact differently would read as a second fact.
 *
 *  The endpoint is reduced to its host: the hint is a sentence, and a full URL
 *  with a path and a version segment in the middle of one is unreadable. A
 *  `baseUrl` that will not parse is shown whole, because at that point the
 *  string itself is the problem worth seeing. */
function describePiProfile(p: PiProfile): string {
  const model = (p.model || '').trim() || 'the model Settings names';
  if (p.auth === 'subscription') {
    return `${model} on the ${(p.subscriptionProvider || 'subscription').trim()} login signed in on this server`;
  }
  let host = (p.baseUrl || '').trim();
  try {
    host = new URL(host).host;
  } catch {
    /* not a URL: say what is stored, which is the fault anyway */
  }
  const wire = p.api === 'openai-responses' ? 'the Responses API' : 'chat completions';
  return host ? `${model} at ${host} over ${wire}, metered` : `${model}, metered`;
}

/** The Provider row's hint. Null profile = the "use the current settings"
 *  option, which is the default and has to say what it will actually do rather
 *  than naming the ⚙ values — those can change between filling this form in and
 *  the build starting, and a hint that quoted them would be the thing that lied.
 *
 *  A CHOSEN profile is quoted in full, because that IS the fact the row just
 *  changed. The metered branch carries the shared-key caveat: a profile stores
 *  no API key (see `LLMSettings.piProfiles`), so every metered setup spends the
 *  one stored Pi key, and pointing this build at a provider that key does not
 *  belong to fails at the first call rather than degrading. The subscription
 *  branch is exempt — it authenticates with a login, not the key — so the
 *  warning is only shown where it is true. */
export function piProfileHint(profile: PiProfile | null): string {
  if (!profile) {
    return 'This build uses the Pi provider Settings names when it starts. Pick a saved setup to send this one build somewhere else without changing Settings.';
  }
  const where = describePiProfile(profile);
  const caveat =
    profile.auth === 'api'
      ? ' A saved setup carries no API key: every metered one spends the single stored Pi key, so if that key does not belong to this provider the build fails to sign in.'
      : '';
  return `This build runs on ${where}. Settings is left as it is, so the next build goes back to it.${caveat}`;
}

/** The saved setup a stored label names, or null. The label is what crosses the
 *  wire, and the list it came from arrives on a settings fetch that can land —
 *  or land again, edited — after the row was answered: a label with nothing
 *  behind it any more is a 422 from the build POST, so it resolves to "use the
 *  current settings" here instead. */
export function findPiProfile(profiles: PiProfile[], label: string): PiProfile | null {
  if (!label) return null;
  return profiles.find((p) => p.label === label) || null;
}

/** What the build intake carries. Which keys are present depends on the path
 *  (uploaded document, guided brief, title-only, deck rebuild) — see the
 *  comments in `submit`, which is the one place that decides. */
/** `POST /api/wizard/source-detail` — the pictures and headings extracted from
 *  an uploaded deck, plus the handle they were read from. Null until it answers,
 *  and null forever if it cannot. */
/** One engine as the build-engine row lists it. */
type EngineRow = { value: string; label: string; available?: boolean; issue?: string; model?: string | null };

export type SourceMedia = {
  uploadId: string;
  media?: { name: string; page?: number }[];
  headings?: string[];
  /** Why a picture pass came back with less than expected — a bonus that
   *  failed is worth saying, never worth failing the intake for. */
  warnings?: string[];
};

export type BuildAnswers = {
  title: string;
  audience: string;
  format: string;
  slideBudget: string;
  slideCount?: number;
  colourTreatment: string;
  baseColour?: string;
  /** Rebuild only, and only ever `true` — absent means "don't". */
  colourMatchSource?: boolean;
  verbatim?: Verbatim;
  overflow?: string;
  /** Sent only when turned OFF; absent means the build's own default (a
   *  divider per section) stands. */
  sectionIntros?: boolean;
  buildQuality?: 'refined' | 'quick';
  /** The STOCK question only: 'stock' or 'none'. Uploads are placed either way
   *  — see the Photography row below. */
  imagery: string;
  /** Only ever `true`; absent means no video. */
  video?: boolean;
  buildModel: string;
  /** Which engine builds this deck. Overrides the ⚙ default for ONE build —
   *  an A/B needs two runs back to back, not a trip to the drawer between. */
  engine?: string;
  /** Pi only: the LABEL of a saved provider setup to build this one deck on,
   *  leaving the ⚙ Pi fields untouched. Absent means "whatever Settings says",
   *  which is the everyday case — the same reason comparing providers is worth
   *  a row here at all is the reason it must not become a saved preference. */
  piProfile?: string;
  /** How many pictures came out of the author's own upload, so the build knows
   *  whether it has photography before it plans. */
  ownMediaCount?: number;
  /** What the author said each picture shows, from the describe step. */
  imageNotes?: SerialisedImageNote[];
  /** Pictures extracted from an uploaded deck that the author binned, by
   *  extracted name. */
  excludeMedia?: string[];
  /** HOW to build it, not WHAT to say — the "Extra instructions" row. Sent only
   *  when the row is open and the author typed something; absent is the
   *  everyday case and the build behaves as it always did. */
  directions?: string;
};

/**
 * The structured new-deck modal — a clean form, not a chat. Drop a brief and
 * many images, set a few options, hit Build. When a brief is present the
 * document is the source of truth: the title and a slide-length estimate are
 * derived from it (via /api/wizard/ingest-brief) and pre-filled, editable.
 *
 * Sections are NOT a question. They follow the brief's own structure and the
 * agent decides them while it plans — a control that guessed a number from
 * heading counts was both wrong and binding (2026-07-31). Whether each section
 * OPENS ON A DIVIDER is a question, and a plain one: the `Section intros`
 * switch.
 *
 * It owns no build lifecycle — on Build it hands a fully-formed `answers`
 * object plus the doc/image File arrays back to the parent (NewDeckAgent),
 * which POSTs build-headless and streams progress exactly as before.
 *
 * Questions the document answers are derived, not asked; length and format are
 * only asked on the WRITTEN-brief path (an upload states its own length, and
 * the agent works it out); audience is only surfaced when the user chooses
 * "adapt & expand" — the one mode where the model rewrites their words.
 *
 * Layout notes (2026-08-01 pass): every option is a labelled row in one card
 * rather than a wrapping field of pills. Binary answers are switches, exclusive
 * answers are segmented controls, and colour is a row of swatches — a nine-chip
 * colour list was the single biggest piece of visual noise in the dialog.
 */
type NewDeckModalProps = {
  /** Hands the intake back to NewDeckAgent, which POSTs build-headless. This
   *  modal owns no build lifecycle. */
  onBuild: (answers: BuildAnswers, docs: File[], imgs: File[], uploads: Record<string, string>) => void;
  onBlank?: () => void;
  onCancel?: () => void;
  stockOk?: boolean;
  videoOk?: boolean;
  /** `build` from GET /api/settings, or null while probing. */
  buildEngines?: EngineChoiceBlock | null;
  /** The saved Pi provider setups, off the same settings fetch. Empty until it
   *  answers, and empty forever on a server where nobody has saved one — which
   *  is the state the Provider row renders nothing at all in. */
  piProfiles?: PiProfile[];
  building?: boolean;
  /** Replaces the elapsed-time note while starting, for something the clock
   *  cannot infer. One sender today: the reupload after a document handle
   *  expired, which restarts the whole upload. Setting it also restarts the
   *  clock, so the time shown belongs to this attempt. */
  buildNote?: string | null;
  /** Why the last attempt to START a build failed (never why a build failed —
   *  that lives on the build's own watch route). Shown in the footer so the
   *  filled-in form survives the failure and can just be submitted again. */
  startError?: string | null;
  /** `{running, maxConcurrent}` from GET /api/wizard/build-status. When every
   *  slot is in use the form still fills in and submits — the server refuses
   *  and says so — but saying it up front beats being refused after a long
   *  upload. */
  slots?: { running: number; maxConcurrent: number } | null;
};

export default function NewDeckModal({
  onBuild,
  onBlank,
  onCancel,
  stockOk = false,
  videoOk = false,
  // `build` from GET /api/settings: {engine, preference, engines:{claude, fast,
  // pi — each {available, issue, model}}}, or null while probing. `engine` is
  // the ⚙ default reduced to something startable; this row overrides it for one
  // build.
  buildEngines = null,
  piProfiles = [],
  building = false,
  buildNote = null,
  startError = null,
  slots = null,
}: NewDeckModalProps) {
  const [docFiles, setDocFiles] = useState<File[]>([]);
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  // What the author said each picture shows, keyed by `noteKey(file)` for an
  // upload and `sourceKey(uploadId, name)` for one that came out of a document.
  // Entries for removed files are harmless — serialisation walks the lists.
  const [imageNotes, setImageNotes] = useState<Record<string, ImageNote>>({});
  // EVERY eligible upload's own pictures and headings, one entry each, in the
  // order the author dropped the files. This was a single `source` until
  // 2026-08-06, chosen by `.find()` — so a second PDF was never even asked
  // about, and its pictures reached the deck unseen and unbinnable (the build's
  // intake extracts inline when the wizard didn't).
  const [sources, setSources] = useState<SourceDetail[]>([]);
  // Pictures from an uploaded document the author has binned, as wire keys
  // (`<uploadId>:<name>` — shared/media-keys.ts), because two documents both
  // name their page-1 picture `slide-01-image1.jpg` and a bare name would bin
  // both.
  const [excludedMedia, setExcludedMedia] = useState<string[]>([]);
  const [notes, setNotes] = useState('');
  // "Extra instructions" — how the deck should be BUILT (structure, order,
  // emphasis), kept apart from the brief because the brief is content and this
  // is not. Folded away by default: most builds want none, and an empty box on
  // screen invites paperwork. Its own switch, so opening it is a decision.
  const [directionsOn, setDirectionsOn] = useState(false);
  const [directions, setDirections] = useState('');
  // Scratch-path guided brief — composed into a synthetic markdown brief so a
  // no-upload deck gives the planner real substance, not just a title.
  const [takeaway, setTakeaway] = useState('');
  const [keyPoints, setKeyPoints] = useState('');
  const [context, setContext] = useState('');
  const [summary, setSummary] = useState<IngestSummary | null>(null);
  const [ingesting, setIngesting] = useState(false);
  // Corrective notice for a dropped file we can't read (.key/.ppt) — dismissible.
  const [fileNotice, setFileNotice] = useState<string | null>(null);
  // Two intake modes: 'upload' (drop a brief / old deck / images) or 'brief'
  // (write it in the form). Switching clears the other mode's inputs so the
  // brief the planner sees always matches what's on screen.
  const [mode, setMode] = useState<'upload' | 'brief'>('upload');
  function switchMode(next: 'upload' | 'brief') {
    if (next === mode) return;
    if (next === 'brief') {
      setDocFiles([]);
      setImageFiles([]);
      setImageNotes({});
      setExcludedMedia([]);
      setSources([]);
      setSummary(null);
    } else {
      setTakeaway('');
      setKeyPoints('');
      setContext('');
    }
    setMode(next);
  }

  const [title, setTitle] = useState('');
  const [lengthMode, setLengthMode] = useState<'auto' | 'set'>('auto'); // 'auto' (planner decides) | 'set'
  // Number OR string: the derive effect sets a number, and a `type="number"`
  // input still hands back a string. Every reader coerces (`Number(slideCount)
  // || 12`), and holding the raw string is what lets the field be cleared and
  // retyped rather than snapping to 0.
  const [slideCount, setSlideCount] = useState<number | string>(12);
  const [format, setFormat] = useState('live'); // written-brief path only
  // Written-brief path only. Typed off BUDGET_HINTS' own keys, so the hint
  // lookup below cannot be asked for a band that has no sentence.
  const [budget, setBudget] = useState<keyof typeof BUDGET_HINTS>('standard');
  const [verbatim, setVerbatim] = useState<Verbatim>('light');
  const [overflow, setOverflow] = useState('split');
  const [audience, setAudience] = useState('team');
  const [colourTreatment, setColourTreatment] = useState('single');
  const [baseColour, setBaseColour] = useState('ORANGE');
  // Rebuild-only "Match my deck" colour default: the builder maps the source
  // deck's accents to RKH tokens. Cleared by picking any explicit colour.
  const [colourMatch, setColourMatch] = useState(true);
  // 'stock' or 'none' — the Photography switch, and ONLY the stock question.
  // It used to also carry 'use' (the author's own uploads), which meant
  // dropping a photo in silently rewrote the answer and turned stock off.
  const [imagery, setImagery] = useState('none');
  const [video, setVideo] = useState(false);
  // A numbered SectionDivider opening each section. On by default (it is what
  // the skill writes); off ships the sections without their intro slides.
  const [sectionIntros, setSectionIntros] = useState(true);
  const [buildQuality, setBuildQuality] = useState<'refined' | 'quick'>('refined');
  // Which engine builds the deck: 'claude' (the default and the product),
  // 'fast' (the ⚙ drawer's one metered model) or 'pi' (whichever provider Pi is
  // pointed at) —
  // both alternatives take the same brief, for comparing the three.
  // Starts on the ⚙ default once it arrives, unless the author already touched
  // the row — a late-landing probe must not undo a deliberate choice.
  const [engine, setEngine] = useState('claude');
  useEffect(() => {
    if (buildEngines?.engine && !touched.current.engine) setEngine(buildEngines.engine);
  }, [buildEngines]);
  // Which SAVED Pi setup this one build runs on, by label. '' is a real answer
  // — "use whatever Settings says" — and the default, so a build started
  // without touching the row behaves exactly as it did before the row existed.
  // Never seeded from the ⚙ fields: those are already the meaning of ''.
  const [piProfile, setPiProfile] = useState('');
  const chosenPiProfile = findPiProfile(piProfiles, piProfile);

  // Track which derived fields the user has overridden, so re-ingesting after
  // adding a file doesn't stomp a value they edited by hand.
  const touched = useRef({ title: false, count: false, verbatim: false, imagery: false, engine: false });

  // Three intake shapes: an uploaded document (the doc is the source of truth —
  // brief-mode length/section controls + a fidelity choice), a user-written
  // brief composed from the scratch-path guided fields, or title-only.
  // `hasUploadedDoc` drives the UI mode; `hasBrief` = there's substance for the
  // planner either way.
  const hasUploadedDoc = docFiles.length > 0;
  const composed = composedBrief();
  const hasGuidedBrief = !hasUploadedDoc && composed.trim().length > 0;
  const hasBrief = hasUploadedDoc || hasGuidedBrief;
  // The ingest preview flagged an uploaded deck (pptx / slide-export PDF):
  // drives the rebuild banner; slide count + verbatim defaults ride the
  // existing derive effect (estimateSlides mirrors the source count).
  const sourceDeck = detectSourceDeck(summary);

  // Photography is ON by default (Jake, 2026-08-01): a deck with pictures is
  // the normal case, and a wizard that defaults to "no photography" quietly
  // ships flat text decks. An explicit choice is never stomped by the settings
  // fetch resolving late.
  //
  // UPLOADS NO LONGER TOUCH IT (Jake, 2026-08-10). Dropping pictures in used to
  // flip this to "use my uploads", which read as the switch answering a
  // different question than the one it asks: someone uploading two of their own
  // photographs to a stock-photography deck got a deck with two photographs.
  // The switch is stock, uploads are placed regardless, and a picture that
  // should not appear is tagged "reference only" in the describe step or
  // binned.
  //
  // EVERY document's pictures, minus anything the author binned. Bytes come from
  // the upload store, so nothing is re-uploaded to be looked at.
  const sourceMedia = useMemo(
    () =>
      sources
        .flatMap((s) => s.media.map((m) => ({ ...m, uploadId: s.uploadId, docName: s.name })))
        .filter((m) => !excludedMedia.includes(mediaKey(m.uploadId, m.name))),
    [sources, excludedMedia],
  );
  useEffect(() => {
    if (touched.current.imagery) return;
    setImagery(stockOk ? 'stock' : 'none');
  }, [stockOk]);

  /** Compose the scratch-path guided fields (+ any pasted extra) into a single
   *  markdown brief. Returns '' when nothing was filled, so a title-only build
   *  stays title-only and the scratch controls stay visible. */
  function composedBrief(): string {
    const parts = [];
    const tk = takeaway.trim();
    const points = keyPoints
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean);
    const cx = context.trim();
    const extra = notes.trim();
    if (tk) parts.push(`## Takeaway\n\n${tk}`);
    if (points.length) parts.push(`## Key points\n\n${points.map((p) => `- ${p}`).join('\n')}`);
    if (cx) parts.push(`## Audience & context\n\n${cx}`);
    if (extra) parts.push(extra);
    if (!parts.length) return '';
    const heading = title.trim() ? `# ${title.trim()}\n\n` : '';
    return heading + parts.join('\n\n');
  }

  /** `name:size` → the id the ingest preview gave that file. Survives every
   *  re-derive, so removing and re-adding a document doesn't cost a re-upload. */
  const uploadIds = useRef(new Map());

  /** The brief docs to ingest / build from: uploaded docs (plus a synthesised
   *  notes file when the user also pasted text), otherwise the scratch-path
   *  brief composed from the guided fields. */
  function briefDocs(): File[] {
    if (docFiles.length) {
      const docs = [...docFiles];
      if (notes.trim()) docs.push(new File([notes], 'pasted-notes.md', { type: 'text/markdown' }));
      return docs;
    }
    const brief = composedBrief();
    return brief.trim() ? [new File([brief], 'brief.md', { type: 'text/markdown' })] : [];
  }

  // What the derive actually depends on: WHICH documents were uploaded, or (on
  // the guided path, where there are none) the brief the user is composing.
  //
  // Deliberately NOT the `notes` textarea. It used to be a dependency, and the
  // notes are appended to the payload as a file, so every pause in typing there
  // re-uploaded and re-parsed every document from scratch. On a 23-page PDF that
  // is ~2 minutes of server-side pdf-parse per keystroke-pause, racing the last
  // one — and on the guided path notes aren't even part of the payload, so the
  // re-parse bought nothing at all. The notes still reach the BUILD (`briefDocs`
  // is what `submit` sends); they just no longer re-derive a title and a slide
  // estimate that a 100-word aside was never going to change.
  const ingestKey = useMemo(
    () => (docFiles.length ? docFiles.map((f) => `${f.name}:${f.size}`).join('|') : composed),
    [docFiles, composed],
  );

  // Derive from the brief, debounced, whenever that set changes.
  useEffect(() => {
    const docs = docFiles.length
      ? docFiles
      : composed.trim()
        ? [new File([composed], 'brief.md', { type: 'text/markdown' })]
        : [];
    if (!docs.length) {
      setSummary(null);
      return;
    }
    let cancelled = false;
    // A superseded derive must stop asking. Without this the browser held the
    // socket open for a parse whose answer it had already decided to ignore,
    // and the abandoned request surfaced as a proxy error and a 500 in the log.
    const ctrl = new AbortController();
    const t = setTimeout(async () => {
      setIngesting(true);
      try {
        const form = new FormData();
        for (const f of docs) form.append('file', f);
        const res = await fetch('/api/wizard/ingest-brief', {
          method: 'POST',
          credentials: 'same-origin',
          body: form,
          signal: ctrl.signal,
        });
        // Deriving is best-effort, so a studio that isn't answering must cost
        // nothing but the derived values: no title, no count, and an empty
        // heading list (the describe step's "belongs with" row hides itself).
        // Parsing before the ok check used to throw here instead, which the
        // bare catch below then swallowed — right outcome, wrong reason.
        const data = res.ok ? await readJson<IngestSummary>(res) : null;
        if (!cancelled && data) {
          setSummary(data);
          // The server kept these bytes; remember the handle so Build can
          // reference them instead of uploading the same file a second time.
          // Never cleared on a miss: the ids are content-addressed server-side,
          // so a stale one either still resolves or is refused outright.
          for (const f of data.files || []) {
            if (f.uploadId) uploadIds.current.set(`${f.name}:${f.size}`, f.uploadId);
          }
          // Sanitised: a title derived from a brief's filename or heading often
          // carries punctuation, which deck names no longer allow. Suggest
          // something already valid rather than making the user fix it.
          if (!touched.current.title) setTitle(sanitiseDeckName(deriveTitle(data, docs)));
          // Pre-fill the "Set a number" value from the brief's STRUCTURE (not
          // raw word count) so switching to manual starts from a sane figure.
          if (!touched.current.count) setSlideCount(estimateSlides(data));
        }
      } catch {
        /* derive is best-effort; the user can still type values */
      } finally {
        if (!cancelled) setIngesting(false);
      }
    }, 450);
    return () => {
      cancelled = true;
      clearTimeout(t);
      ctrl.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ingestKey]);

  // Fidelity advice, recomputed as the brief or the chosen length changes: a
  // brief that can't fill the deck it's being asked for needs "adapt & expand",
  // because `light`/`strict` forbid the agent adding anything and its only
  // remaining lever is emptier slides. Keyed off the length the user has
  // ACTUALLY chosen (an explicit count, else the estimate), since the same
  // brief is thin or ample depending on how long a deck it has to carry.
  const verbatimAdvice = useMemo(() => {
    if (!hasUploadedDoc || sourceDeck || !summary) return null;
    const target = lengthMode === 'set' ? Number(slideCount) || undefined : undefined;
    const rec = recommendVerbatim(summary, target);
    if (!rec) return null;
    const words = Math.round((summary.totalText || 0) / 5);
    const per = Math.round(words / Math.max(1, target || estimateSlides(summary)));
    return { ...rec, perSlide: per };
  }, [hasUploadedDoc, sourceDeck, summary, lengthMode, slideCount]);

  // Pre-select the advice, but never override a choice the user has made.
  useEffect(() => {
    if (verbatimAdvice && !touched.current.verbatim) setVerbatim(verbatimAdvice.value);
  }, [verbatimAdvice]);

  // Image thumbnail object URLs, cleaned up on change/unmount.
  const thumbs = useMemo(() => imageFiles.map((f) => ({ f, url: URL.createObjectURL(f) })), [imageFiles]);
  useEffect(() => () => thumbs.forEach((t) => URL.revokeObjectURL(t.url)), [thumbs]);

  // The brief's own headings, offered as "belongs with" choices in the describe
  // step. These come from the ingest preview the modal already fetches, so they
  // are the author's words — the one placement vocabulary that exists before the
  // deck does. An uploaded DECK contributes none here: the preview reads three
  // pages, and a heading list that stops there offers sections the deck does not
  // have. Those arrive from `sources` below instead — from ALL of them, in drop
  // order, or the dropdown cannot offer a section that exists only in the second
  // document.
  const briefHeadings = useMemo(() => {
    const out = [];
    const seen = new Set();
    for (const h of sources.flatMap((s) => s.headings)) {
      const k = h.toLowerCase();
      if (seen.has(k)) continue;
      seen.add(k);
      out.push(h);
    }
    for (const f of summary?.files || []) {
      for (const h of f.headings || []) {
        const k = h.toLowerCase();
        if (seen.has(k)) continue;
        seen.add(k);
        out.push(h);
      }
    }
    return out;
  }, [summary, sources]);

  // The uploaded deck's OWN pictures, and its full heading list — both too slow
  // to belong in the preview (a whole-document text pass, and for a PDF a
  // Chromium visit over every page), so they are asked for separately once the
  // preview has come back and named a deck.
  //
  // Entirely best-effort. No answer, a server that isn't there, an upload the
  // store never took: the describe step then lists only what the author added,
  // which is what it did before any of this existed.
  // ANY upload the store took that could be carrying pictures — not just the
  // source deck. `f.deck && f.uploadId` was the test until 2026-08-04, and it
  // meant a portrait or text-dense PDF never even asked: the fetch below never
  // fired, `source` stayed null, and the describe step had nothing to show and
  // no reason to give. Two server-side gates were fixed the same day; this was
  // the third, and on its own it made the other two invisible. Extraction is
  // not a rebuild feature — a photograph in a brief is as usable on a slide as
  // one in a deck export.
  //
  // EVERY eligible upload, not just the first (2026-08-06). This was `.find()`,
  // and everything downstream was singular to match — so a second document was
  // never asked about, never appeared, and could not be binned or described.
  // Its pictures still reached the deck: the intake extracts inline for any doc
  // the wizard didn't get to, so the effect was pictures the author never saw.
  const sourceFiles = useMemo(
    () => (summary?.files || []).filter((f) => f.uploadId && (f.deck || f.type === 'pdf')),
    [summary],
  );
  // Stable dep. `summary` is a fresh object on every debounced preview, and what
  // hangs off this effect is minutes of Chromium.
  const sourceIdKey = sourceFiles.map((f) => f.uploadId).join(',');
  useEffect(() => {
    const wanted = sourceFiles.map((f) => ({ uploadId: f.uploadId as string, name: f.name }));
    if (!wanted.length) {
      setSources([]);
      return;
    }
    let cancelled = false;
    const ctrl = new AbortController();
    // Seed the whole list up front, carrying over anything already answered so
    // its pictures stay on screen while the set is re-asked. Re-asking is cheap:
    // `sourceDetail` short-circuits on the upload's own detail.json.
    setSources((prev) =>
      wanted.map((w) => {
        const had = prev.find((p) => p.uploadId === w.uploadId);
        return had ? { ...had, name: w.name, busy: true } : { ...w, busy: true, media: [], headings: [], warnings: [] };
      }),
    );
    (async () => {
      // ONE AT A TIME, in the order the author dropped them. Not for politeness:
      // `renderPdfPages` already runs through `queueRenderWork`, which serialises
      // every Chromium render server-side (two at once is what SIGKILLed two
      // vite-pool children, code 137, 2026-08-03). Firing them together would
      // make nothing faster, would move the queue somewhere the UI cannot see
      // it, and would park requests against the browser's ~6-per-origin cap that
      // the thumbnails of the FIRST document then have to wait behind.
      for (const w of wanted) {
        if (cancelled) return;
        let got: Omit<SourceMedia, 'uploadId'> | null = null;
        try {
          const res = await fetch('/api/wizard/source-detail', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ uploadId: w.uploadId }),
            signal: ctrl.signal,
          });
          got = res.ok ? await readJson<Omit<SourceMedia, 'uploadId'>>(res) : null;
        } catch {
          /* a document's own pictures are a bonus, never a requirement */
        }
        if (cancelled) return;
        // Settled either way: a failed fetch has no answer coming, and a
        // document left marked busy would hold the "still working" line open
        // forever.
        setSources((prev) =>
          prev.map((s) =>
            s.uploadId === w.uploadId
              ? {
                  ...s,
                  busy: false,
                  media: got?.media || [],
                  headings: got?.headings || [],
                  warnings: got?.warnings || [],
                }
              : s,
          ),
        );
      }
    })();
    return () => {
      cancelled = true;
      ctrl.abort();
    };
    // `sourceIdKey` is the stable projection of `sourceFiles`. Depending on the
    // array itself would re-run this — a Chromium pass per document — on every
    // debounced preview, since `summary` is a fresh object each time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceIdKey]);

  // One list for the describe step, the documents' own pictures first: they are
  // the ones the author recognises, and seeing them is what tells them the
  // rebuild kept its photography at all. Grouped by document in drop order, each
  // carrying the upload it came out of.
  const noteItems = useMemo(
    () => [
      ...sourceMedia.map((m) => ({
        key: sourceKey(m.uploadId, m.name),
        mediaKey: mediaKey(m.uploadId, m.name),
        name: m.name,
        docName: m.docName,
        page: m.page,
        source: true,
        // THE OWNING UPLOAD, never a shared one. `readUploadMedia` checks the
        // name against that id's own manifest, and both documents hold a
        // `slide-01-image1.jpg` — so the wrong id here does not 404, it quietly
        // serves the other document's picture.
        url: `/api/wizard/upload-media/${m.uploadId}/${encodeURIComponent(m.name)}`,
      })),
      ...thumbs.map(({ f, url }) => ({ key: noteKey(f), name: f.name, file: f, url })),
    ],
    [sourceMedia, thumbs],
  );

  function addFiles(fileList: FileList | File[] | null) {
    const docs: File[] = [];
    const imgs: File[] = [];
    for (const f of fileList || []) {
      // Presentation formats we can't read: tell the user what to export
      // instead of silently dropping their deck (mirrors the server's 422s).
      if (KEY_RE.test(f.name)) {
        setFileNotice(KEYNOTE_UPLOAD_NOTICE);
        continue;
      }
      if (LEGACY_PPT_RE.test(f.name)) {
        setFileNotice(LEGACY_PPT_UPLOAD_NOTICE);
        continue;
      }
      if (IMG_RE.test(f.name)) imgs.push(f);
      else if (DOC_RE.test(f.name)) {
        // Image credits / licence / readme sidecars aren't the brief — skip them
        // so they can't become the deck title or bloat the content.
        if (isSidecarDoc(f)) continue;
        docs.push(f);
      }
    }
    if (docs.length) setDocFiles((cur) => dedupe([...cur, ...docs]));
    if (imgs.length) setImageFiles((cur) => dedupe([...cur, ...imgs]));
  }
  const removeDoc = (f: File) => setDocFiles((cur) => cur.filter((x) => x !== f));
  const removeImg = (f: File) => setImageFiles((cur) => cur.filter((x) => x !== f));

  // Anything the user has put in. Closing this dialog throws all of it away and
  // there is no draft to come back to, so the two ACCIDENTAL exits are shut off
  // once the form holds work: a backdrop click (never closes — Jake, 2026-08-01)
  // and Escape. The ✕ and Cancel buttons are deliberate and always close.
  const dirty =
    docFiles.length > 0 ||
    imageFiles.length > 0 ||
    Boolean(title.trim() || takeaway.trim() || keyPoints.trim() || context.trim() || notes.trim());
  const escState = useRef({ onCancel, dirty });
  escState.current = { onCancel, dirty };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || escState.current.dirty) return;
      escState.current.onCancel?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // The deck's name has to satisfy the same rules as rename/duplicate, and it
  // has to be free — the server refuses a clash rather than silently suffixing
  // the slug, so catching it here saves the user the whole intake form.
  const checkName = useDeckNameCheck();
  const nameError = useMemo(() => (title.trim() ? checkName(title) : null), [title, checkName]);
  const canBuild = title.trim().length > 0 && !nameError && !building;

  // Every build slot in use. Deliberately NOT a disabled button: the wizard
  // used to refuse to render at all while the server was at capacity, which
  // (once more than one build could run) meant a full builder locked everyone
  // out of even filling the form. Say it, let them submit, let the server rule.
  const slotsFull = !!slots && slots.maxConcurrent > 0 && slots.running >= slots.maxConcurrent;

  const footHint = slotsFull
    ? `All ${slots?.maxConcurrent} build slots are in use. Fill this in anyway: it starts as soon as one frees up.`
    : sourceDeck
      ? 'The agent rebuilds your uploaded deck slide by slide in the RKH style.'
      : hasUploadedDoc
        ? 'The agent plans the deck from your brief, then writes every slide.'
        : hasGuidedBrief
          ? 'The agent plans the deck from what you told it, then writes every slide.'
          : 'Builds from your title alone: add a takeaway and key points for a stronger deck.';

  // Seconds since the Build press. The POST that starts a build is NOT quick:
  // it reads the multipart upload, converts every document, renders a PDF's
  // pages and sends the images to a vision model before it can answer with a
  // job id — tens of seconds, and minutes for a long PDF. A disabled button
  // reading "Starting…" is not enough feedback for that: with nothing moving,
  // it reads as a hang and people reload the page, which abandons the request
  // mid-upload. A ticking clock is the cheapest honest proof of life, since the
  // request is opaque until it returns.
  const [startSecs, setStartSecs] = useState(0);
  // `buildNote` is in the deps so a note change RESTARTS the clock: the one
  // sender is the reupload, which throws the first attempt away and sends every
  // file again. Counting on from the first press would show 1:20 for work that
  // just began.
  useEffect(() => {
    if (!building) {
      setStartSecs(0);
      return undefined;
    }
    const began = Date.now();
    setStartSecs(0);
    const id = setInterval(() => setStartSecs(Math.round((Date.now() - began) / 1000)), 1000);
    return () => clearInterval(id);
  }, [building, buildNote]);

  // What the server is actually doing, in the user's terms — and it escalates,
  // because "this is slow" and "this is stuck" look identical otherwise. A
  // caller-supplied note wins: it knows things the clock cannot infer.
  const startingNote =
    buildNote ||
    (() => {
      const heavy = docFiles.length > 0 || imageFiles.length > 0;
      if (startSecs >= 45) {
        return heavy
          ? 'Still reading your files. A long PDF or a lot of images can take a few minutes — leaving this page cancels it.'
          : 'Still setting up. Leaving this page cancels it.';
      }
      if (startSecs >= 15) {
        return heavy ? 'Reading your brief and images…' : 'Setting the build up…';
      }
      return heavy ? 'Uploading your files…' : 'Setting the build up…';
    })();

  function submit() {
    if (!canBuild) return;
    const count = Number(slideCount) || 12;
    // Length: 'auto' lets the planner lay the deck out from the document's own
    // structure (no forced count, just a band centred on the structure-aware
    // estimate); 'set' pins an explicit count the planner must hit.
    const autoLength = hasUploadedDoc && lengthMode === 'auto';
    const estimate = estimateSlides(summary);
    // A user-written (guided) brief always wants the planner to EXPAND thin
    // intent into a full deck, so it ships verbatim:'source' + overflow:'split'.
    // An uploaded document keeps the user's fidelity choice (default 'light').
    // A deck rebuild sends NO fidelity/overflow — those controls are hidden,
    // and the server's rebuild defaults own them (verbatim 'light', overflow
    // 'split', structure mirrored from the source). `sectionCount` is never
    // sent from this modal at all; the intake field survives for API callers
    // that genuinely want to pin one.
    const briefVerbatim = hasUploadedDoc ? verbatim : 'source';
    const a: BuildAnswers = {
      title: title.trim(),
      audience: hasUploadedDoc && !sourceDeck && verbatim === 'source' ? audience : 'team',
      format: hasUploadedDoc ? 'live' : format,
      slideBudget: hasUploadedDoc ? budgetForCount(autoLength ? estimate : count) : budget,
      ...(hasUploadedDoc && !autoLength ? { slideCount: count } : {}),
      colourTreatment,
      ...(colourTreatment === 'single' ? { baseColour } : {}),
      ...(sourceDeck && colourMatch ? { colourMatchSource: true } : {}),
      ...(hasBrief && !sourceDeck ? { verbatim: briefVerbatim, overflow: hasUploadedDoc ? overflow : 'split' } : {}),
      // Only sent when turned OFF: absent means the build's own default (a
      // divider per section) stands.
      ...(sectionIntros ? {} : { sectionIntros: false }),
      ...(engine === 'codex' ? { buildQuality } : {}),
      imagery,
      ...(video ? { video: true } : {}),
      // The ⚙ Deck builds choice, with the constant as the fallback while the
      // settings probe is still out.
      buildModel: buildEngines?.engines?.claude?.model || BUILD_MODEL,
      // Only sent when the author picked an engine other than the default.
      // OpenCode and Pi read their own ⚙ model server-side, so buildModel is
      // inert on either.
      ...(engine !== 'claude' ? { engine } : {}),
      // Pi only, and only when a saved setup was actually picked — the row is
      // hidden on the other engines, so a label left behind by a visit to Pi
      // must not travel with a Claude build. Resolved rather than sent raw: a
      // label whose setup has since been deleted is a 422, and the honest
      // answer to that is the ⚙ provider, not a refused build.
      ...(engine === 'pi' && chosenPiProfile ? { piProfile: chosenPiProfile.label } : {}),
    };
    // Only when the describe step was actually on screen: a note left over from
    // before the user turned photography off — or from before they closed the
    // step again — would otherwise steer a build whose form never showed it.
    if (describeOn) {
      const notes = serialiseImageNotes(imageFiles, imageNotes, sourceMedia);
      if (notes.length) a.imageNotes = notes;
    }
    // Only while the row is open, for the same reason as the image notes: an
    // instruction the author folded away is one they took back.
    if (directionsOn && directions.trim()) a.directions = directions.trim().slice(0, MAX_DIRECTIONS);
    // Sent whatever else is on screen: binning a picture from the uploaded deck
    // is an explicit instruction, and it can only ever remove one.
    if (excludedMedia.length) a.excludeMedia = excludedMedia;
    // Not an intake answer — the agent strips it. It is how the caller learns
    // there are pictures at all when none of them came through the browser.
    if (sourceMedia.length) a.ownMediaCount = sourceMedia.length;
    // Every doc goes across, plus the handles the server gave for the ones it
    // already holds. The agent decides which to actually upload — it has to keep
    // the files regardless, so it can re-send them if a handle has expired.
    onBuild(a, briefDocs(), imageFiles, Object.fromEntries(uploadIds.current));
  }

  const fileRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  const setImageryChoice = (v: string) => {
    touched.current.imagery = true;
    setImagery(v);
  };
  const photographyOn = imagery !== 'none';
  const hasOwnPictures = imageFiles.length > 0 || sourceMedia.length > 0;
  // Whether describing the pictures could change anything: there are pictures.
  // NOT gated on the Photography switch any more — that switch is about stock,
  // and the describe step is the only place an upload can be marked reference
  // only, so hiding it with stock off hid the one control that answers "don't
  // put this on a slide". A rebuild is deliberately INCLUDED. This used to carry
  // `&& !sourceDeck`, on the reasoning that a rebuild's extracted media already
  // name the slide they came from (slide-03-image2.png) — true of the extracted
  // media, and irrelevant here, because those never reach this list. `thumbs`
  // comes from `imageFiles`, which only ever holds the author's own picks; the
  // effect was that uploading a deck silently removed the one place they could
  // describe the pictures they added on top of it (Alex, 2026-08-04).
  const canDescribe = hasOwnPictures;
  // …and whether they ASKED to. Off by default: a PDF with two pictures in it
  // used to put a form on screen before anyone wanted one, which reads as
  // paperwork standing between the author and their deck. The resting state is
  // the plain grid below; this opens the form over it.
  const [describeOn, setDescribeOn] = useState(false);

  // What the resting grid shows. When describing is available it is the SAME
  // list the form would show, the deck's own extracted pictures included — so
  // binning one never requires opening the form first. That was the real cost of
  // the form-by-default: ImageNotes was the only place an extracted picture
  // could be excluded, and it took a step the author never asked for to get
  // there. The fallback branch is now only the no-pictures case, which shows
  // nothing at all.
  const gridItems: NoteItem[] = canDescribe
    ? noteItems
    : thumbs.map(({ f, url }) => ({ key: noteKey(f), name: f.name, file: f, url }));

  /** The bin means two different things. An upload the author picked can simply
   *  be dropped; a picture that came out of their deck was never theirs to
   *  withhold, so it is recorded as an exclusion the intake honours. */
  function removeNoteItem(item: NoteItem) {
    // The WIRE KEY, not the name: two documents both hold a `slide-01-image1.jpg`
    // and a bare name would bin the pair of them.
    const key = item.mediaKey;
    if (item.source && key) setExcludedMedia((prev) => (prev.includes(key) ? prev : [...prev, key]));
    else if (item.file) removeImg(item.file);
  }
  const status = sourceStatus(sources);
  // Whether a thumbnail needs to say which document it came out of. With one
  // source it would be noise on every tile answering a question nobody has.
  const manySources = new Set(sourceMedia.map((m) => m.uploadId)).size > 1;
  const verbatimHint = VERBATIM_CHIPS.find((c) => c.value === verbatim)?.hint || '';
  // The picked accent is ignored under either of these, so the picker is shown
  // disabled rather than pretending to matter.
  const colourInert = colourTreatment === 'sections' || Boolean(sourceDeck && colourMatch);

  // The engines this server can build on besides Claude, in the order they were
  // added. Both are gated on the engine's BLOCK existing, never on `.model`: one
  // that can't start right now appears disabled carrying the server's own
  // reason, so the choice can never fail on click.
  //
  // OpenCode was gated on `.model` until 2026-08-06, which hid it on exactly the
  // servers that needed telling. Its unset model IS what stops it, and
  // `fastBuildIssue()` says so in words ("No OpenCode model chosen, pick one in
  // the ⚙ drawer"), but hiding the row hid the sentence, so the studio went
  // quiet instead of answering.
  const engineBlocks = buildEngines?.engines || {};
  const altEngines = [
    engineBlocks.fast && { value: 'fast', label: 'OpenCode', ...engineBlocks.fast },
    engineBlocks.pi && { value: 'pi', label: 'Pi', ...engineBlocks.pi },
    engineBlocks.codex && { value: 'codex', label: 'Codex', ...engineBlocks.codex },
  ].filter((e): e is EngineRow => Boolean(e));
  // Three segments no longer fit beside the label, so the row stacks and wraps.
  const engineRowWide = altEngines.length > 1;
  const blockedEngines = altEngines.filter((e) => !e.available && e.issue);

  return (
    <Modal
      ariaLabel="Create a deck"
      onClose={() => onCancel?.()}
      className="modal--new-deck"
      // This wizard keeps Escape (see the escState effect above): it closes
      // only while the form is untouched, and Modal cannot know that. The
      // backdrop has never dismissed it, for the same reason — a long intake
      // form should not be thrown away by a stray click beside it.
      escape="none"
      closeOnBackdrop={false}
    >
      <header className="modal__header new-deck__head">
        <h2 className="modal__title modal__title--sm" id="new-deck-title">
          Create a deck
        </h2>
        <span className="add-slide__spacer" />
        {onBlank && (
          <button type="button" className="link small" onClick={onBlank} disabled={building}>
            Start blank instead
          </button>
        )}
        <button type="button" className="iconbtn" onClick={onCancel} aria-label="Close" title="Close">
          <X />
        </button>
      </header>

      <div className="modal__body new-deck__body">
        {/* ---------- Mode: upload vs write a brief ---------- */}
        <div className="new-deck__modes" role="tablist" aria-label="How to start">
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'upload'}
            className={'new-deck__mode' + (mode === 'upload' ? ' new-deck__mode--active' : '')}
            onClick={() => switchMode('upload')}
            disabled={building}
          >
            <span className="new-deck__mode-name">Upload</span>
            <span className="new-deck__mode-sub">A brief, an old deck, or images</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'brief'}
            className={'new-deck__mode' + (mode === 'brief' ? ' new-deck__mode--active' : '')}
            onClick={() => switchMode('brief')}
            disabled={building}
          >
            <span className="new-deck__mode-name">Write a brief</span>
            <span className="new-deck__mode-sub">Describe the deck in the form</span>
          </button>
        </div>

        {/* ---------- Content (upload mode) ---------- */}
        {mode === 'upload' && (
          <section className="new-deck__section">
            {/* Once something has been dropped the zone has done its job, so
                  it shrinks to a slim "add more" bar and gives the room back to
                  the file list and the options. */}
            <div
              className={
                'assets__drop new-deck__drop' +
                (docFiles.length || imageFiles.length ? ' new-deck__drop--compact' : '') +
                (dragOver ? ' assets__drop--over' : '')
              }
              onClick={() => fileRef.current?.click()}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                addFiles(e.dataTransfer?.files || []);
              }}
            >
              <span className="new-deck__drop-mark" aria-hidden="true">
                <Plus />
              </span>
              <span className="new-deck__drop-title">
                {docFiles.length || imageFiles.length ? 'Add more files' : 'Drop your brief and images here'}
              </span>
              <button
                type="button"
                className="btn btn--primary btn--sm"
                onClick={(e) => {
                  e.stopPropagation();
                  fileRef.current?.click();
                }}
              >
                Choose files
              </button>
              <span className="muted small new-deck__drop-formats">
                PDF · Word · Markdown · text · PNG · JPG · WEBP · GIF · SVG · MP4
              </span>
              <span className="muted small new-deck__drop-hint">
                Got an old deck? Drop the PowerPoint (or a PDF export) and we’ll rebuild it in the RKH style.
              </span>
              <input
                ref={fileRef}
                type="file"
                accept=".pdf,.md,.txt,.markdown,.docx,.pptx,.ppsx,.key,.ppt,.pps,image/*,video/mp4,video/webm"
                multiple
                style={{ display: 'none' }}
                onChange={(e) => {
                  addFiles(e.target.files);
                  e.target.value = '';
                }}
              />
            </div>

            {fileNotice && (
              <p className="new-deck__file-notice muted small" role="status">
                {fileNotice}{' '}
                <button type="button" className="link" onClick={() => setFileNotice(null)}>
                  Dismiss
                </button>
              </p>
            )}

            {sourceDeck && (
              <p className="new-deck__rebuild-note small" role="status">
                <strong>Deck rebuild.</strong> We read all {sourceDeck.slideCount} slides of your{' '}
                {sourceDeck.kind === 'pptx' ? 'PowerPoint' : 'PDF deck'}: the build keeps their content and order and
                redesigns every slide in the RKH style.
              </p>
            )}
            {/* A PDF was excluded from this regex, so a PDF deck upload — the
                  slowest case there is — showed nothing at all while it waited,
                  and the Title field is filled from this response, so the user
                  is stuck looking at a form that appears to be doing nothing. */}
            {!sourceDeck && ingesting && docFiles.some((f) => /\.(pptx|ppsx|pdf)$/i.test(f.name)) && (
              <p className="new-deck__rebuild-note small" role="status">
                {docFiles.some((f) => /\.(pptx|ppsx)$/i.test(f.name))
                  ? 'Reading your PowerPoint…'
                  : 'Reading your PDF…'}
              </p>
            )}

            {docFiles.length > 0 && (
              <ul className="new-deck__files" role="list">
                {docFiles.map((f) => (
                  <li key={f.name + f.size} className="new-deck__file">
                    <Paperclip />
                    <span className="new-deck__file-name">{f.name}</span>
                    <button
                      type="button"
                      className="iconbtn"
                      onClick={() => removeDoc(f)}
                      aria-label={`Remove ${f.name}`}
                    >
                      <Trash />
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {/* The picture pass, which runs AFTER the preview has answered and
                  takes a Chromium visit per page. Three states, because two of
                  them used to look the same: still working, found none, and
                  could not look. A step that simply never appears is what sent
                  someone hunting through the server for an afternoon. The rules
                  for saying all this about several documents at once live in
                  `sourceStatus`, where they can be tested. */}
            {status.busy && (
              <p className="new-deck__rebuild-note small" role="status">
                Extracting images from {status.busy.name || 'your upload'}…
                {status.total > 1 ? ` (${status.busy.position} of ${status.total})` : ''}
                {/* A queue of documents is a wait worth being able to walk away
                      from, and this is true: the build extracts anything the
                      wizard did not get to. Leaving early costs the chance to
                      have described those pictures, nothing else. */}
                {status.total > 1 && (
                  <> You can start the build without waiting — we’ll keep the pictures either way.</>
                )}
              </p>
            )}
            {!status.busy && status.empty.length > 0 && (
              <p className="new-deck__rebuild-note small" role="status">
                No pictures found in {listNames(status.empty)} — add your own below if you have them.
              </p>
            )}
            {!status.busy &&
              status.warnings.map((w) => (
                <p key={w.id} className="new-deck__rebuild-note small" role="status">
                  {w.text}
                </p>
              ))}

            {/* Describing the pictures is OPT-IN. The resting state is the
                  grid below, which is everything most authors want: see what
                  came in, bin what shouldn't. The switch is offered only when
                  an answer could change the deck. */}
            {canDescribe && (
              <OptionRows>
                <OptionRow
                  label="Describe these pictures"
                  hint={
                    describeOn
                      ? 'Say what each one shows, and mark any that are reference material rather than deck imagery.'
                      : 'Optional. Off, every picture is placed where it fits.'
                  }
                >
                  <Toggle checked={describeOn} onChange={setDescribeOn} label="Describe these pictures" />
                </OptionRow>
              </OptionRows>
            )}

            {canDescribe && describeOn ? (
              <ImageNotes
                items={noteItems}
                headings={briefHeadings}
                notes={imageNotes}
                onChange={setImageNotes}
                onRemove={removeNoteItem}
              />
            ) : gridItems.length > 0 ? (
              <ul className="new-deck__thumbs" role="list">
                {gridItems.map((item) => (
                  <li
                    key={item.key}
                    className="new-deck__thumb"
                    title={item.docName && manySources ? `${item.name} — from ${item.docName}` : item.name}
                  >
                    {/\.(mp4|webm|mov)$/i.test(item.name) ? (
                      <video src={item.url} muted loop playsInline />
                    ) : (
                      <img src={item.url} alt={item.name} />
                    )}
                    {/* Which document this came out of, said only when more
                          than one contributed — the pictures are already in
                          drop order, so this names the run rather than breaking
                          the grid into a row-block per document. */}
                    {manySources && item.docName && <span className="new-deck__thumb-from">{item.docName}</span>}
                    <button
                      type="button"
                      className="iconbtn new-deck__thumb-x"
                      onClick={() => removeNoteItem(item)}
                      aria-label={
                        item.source
                          ? `Leave out ${item.name}${item.docName && manySources ? ` from ${item.docName}` : ''}`
                          : `Remove ${item.name}`
                      }
                    >
                      <Trash />
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}

            {hasUploadedDoc && (
              <textarea
                className="new-deck__notes"
                rows={3}
                placeholder="…add any notes to go alongside your brief"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            )}
          </section>
        )}

        {/* ---------- Title ---------- */}
        <label className="settings__field new-deck__title-field">
          <span>Title{ingesting ? <span className="muted"> · reading your brief…</span> : null}</span>
          <input
            type="text"
            value={title}
            placeholder="What's the deck about?"
            onChange={(e) => {
              touched.current.title = true;
              setTitle(e.target.value);
            }}
            maxLength={80}
            aria-invalid={nameError ? 'true' : undefined}
          />
          {nameError ? (
            <span className="small error" role="alert">
              {nameError}
            </span>
          ) : title.trim() ? (
            <span className="muted small">
              Link <code>/p/{deckSlugFor(title)}/</code>
            </span>
          ) : null}
        </label>

        {/* ---------- Tell us about your deck (brief mode) ---------- */}
        {mode === 'brief' && (
          <section className="new-deck__section">
            <h3 className="settings__heading">Tell us about your deck</h3>
            <p className="muted small" style={{ marginTop: 0 }}>
              Optional, but a takeaway and a few key points make a far stronger deck than a title alone: the planner
              builds from what you give it here.
            </p>
            <label className="settings__field">
              <span>What’s the one takeaway?</span>
              <input
                type="text"
                value={takeaway}
                placeholder="The single thing the audience should remember"
                onChange={(e) => setTakeaway(e.target.value)}
              />
            </label>
            <label className="settings__field">
              <span>Key points to land</span>
              <textarea
                className="new-deck__notes"
                rows={4}
                value={keyPoints}
                placeholder="One per line: the points the deck must make"
                onChange={(e) => setKeyPoints(e.target.value)}
              />
            </label>
            <label className="settings__field">
              <span>
                Audience &amp; context <span className="muted">(optional)</span>
              </span>
              <input
                type="text"
                value={context}
                placeholder="Who it’s for, and any background that shapes it"
                onChange={(e) => setContext(e.target.value)}
              />
            </label>
            <label className="settings__field">
              <span>
                Anything else to include <span className="muted">(optional)</span>
              </span>
              <textarea
                className="new-deck__notes"
                rows={3}
                value={notes}
                placeholder="Paste any extra info, figures, or wording you want used"
                onChange={(e) => setNotes(e.target.value)}
              />
            </label>
          </section>
        )}

        {/* ---------- Options ---------- */}
        <section className="new-deck__section">
          <h3 className="settings__heading">Options</h3>
          <OptionRows>
            {/* Length. An UPLOAD states its own length, so the only question
                  is "match it, or pin a number?" — and until a file is dropped
                  there is nothing to ask at all (Jake, 2026-08-01: uploaded
                  content should just be worked out). A WRITTEN brief has no
                  length of its own, so that path is asked outright. */}
            {hasUploadedDoc ? (
              <OptionRow
                label="Length"
                hint={
                  lengthMode === 'auto'
                    ? sourceDeck
                      ? `One new slide per source slide, about ${estimateSlides(summary)}.`
                      : `The agent lays the deck out from your brief’s own structure${
                          summary ? `, about ${estimateSlides(summary)} slides` : ''
                        }.`
                    : 'The build hits this number of slides.'
                }
              >
                <Segmented
                  value={lengthMode}
                  onChange={setLengthMode}
                  options={[
                    { value: 'auto', label: sourceDeck ? 'Match my deck' : 'Work it out' },
                    { value: 'set', label: 'Set a number' },
                  ]}
                />
                {lengthMode === 'set' && (
                  <span className="new-deck__stepper">
                    <button
                      type="button"
                      className="iconbtn"
                      aria-label="Fewer slides"
                      onClick={() => {
                        touched.current.count = true;
                        setSlideCount((n) => Math.max(3, Number(n) - 1));
                      }}
                    >
                      <Minus />
                    </button>
                    <input
                      type="number"
                      min={3}
                      max={60}
                      aria-label="Number of slides"
                      value={slideCount}
                      onChange={(e) => {
                        touched.current.count = true;
                        setSlideCount(e.target.value);
                      }}
                    />
                    <button
                      type="button"
                      className="iconbtn"
                      aria-label="More slides"
                      onClick={() => {
                        touched.current.count = true;
                        setSlideCount((n) => Math.min(60, Number(n) + 1));
                      }}
                    >
                      <Plus />
                    </button>
                  </span>
                )}
              </OptionRow>
            ) : mode === 'brief' ? (
              <>
                <OptionRow label="Length" hint={BUDGET_HINTS[budget]}>
                  <Segmented
                    value={budget}
                    onChange={setBudget}
                    options={[
                      { value: 'short', label: 'Short' },
                      { value: 'standard', label: 'Standard' },
                      { value: 'deep', label: 'Deep' },
                    ]}
                  />
                </OptionRow>
                <OptionRow
                  label="How it’s shown"
                  hint={
                    format === 'live'
                      ? 'Presented live: slides support you, they don’t replace you.'
                      : 'Read on their own: the slides have to carry the detail.'
                  }
                >
                  <Segmented
                    value={format}
                    onChange={setFormat}
                    options={[
                      { value: 'live', label: 'Presented' },
                      { value: 'readout', label: 'Read async' },
                    ]}
                  />
                </OptionRow>
              </>
            ) : null}

            {hasUploadedDoc && !sourceDeck && (
              <OptionRow
                label="Your words"
                hint={[
                  // What the SELECTED mode does always leads, so the three
                  // chips never read identically; any thin-brief advice is a
                  // second sentence, not a replacement.
                  verbatimHint,
                  verbatimAdvice && verbatimAdvice.value === 'source' && verbatim !== 'source'
                    ? `Your brief is light for a deck this long (about ${verbatimAdvice.perSlide} words per slide), so the slides will be sparser.`
                    : verbatimAdvice && verbatim === verbatimAdvice.value
                      ? verbatimAdvice.reason
                      : null,
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                <Segmented
                  value={verbatim}
                  onChange={(v) => {
                    touched.current.verbatim = true;
                    setVerbatim(v);
                  }}
                  options={VERBATIM_CHIPS.map((c) => ({ value: c.value, label: c.label, title: c.hint }))}
                />
              </OptionRow>
            )}

            {hasUploadedDoc && !sourceDeck && (
              <OptionRow
                label="If a slide gets heavy"
                hint={
                  overflow === 'split'
                    ? 'Nothing is dropped: heavy slides split across two.'
                    : 'Tightened to fit the slide count.'
                }
              >
                <Segmented
                  value={overflow}
                  onChange={setOverflow}
                  options={[
                    { value: 'split', label: 'Keep it all' },
                    { value: 'condense', label: 'Tighten' },
                  ]}
                />
              </OptionRow>
            )}

            {hasUploadedDoc && !sourceDeck && verbatim === 'source' && (
              <OptionRow label="Audience" hint="Shapes the tone the agent writes in.">
                <select value={audience} onChange={(e) => setAudience(e.target.value)} aria-label="Audience">
                  {AUDIENCE_CHIPS.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </OptionRow>
            )}

            {/* Photography — the STOCK switch, on by default. It says nothing
                  about the author's own pictures: those are placed whenever
                  they are here, on or off, which is why the hint spells out
                  what happens to them rather than leaving the switch to imply
                  it. Held back one at a time in the describe step above
                  ("reference only"), or binned. */}
            <OptionRow
              label="Photography"
              hint={
                !stockOk
                  ? 'No stock provider configured: uploaded pictures are still placed.'
                  : photographyOn
                    ? hasOwnPictures
                      ? 'Stock photos on the slides that earn one, alongside your own pictures.'
                      : 'Stock photos on the slides that earn one.'
                    : hasOwnPictures
                      ? 'No stock photos: your own pictures only.'
                      : 'Text and primitives only.'
              }
            >
              <Toggle
                checked={photographyOn}
                disabled={!stockOk}
                label="Photography"
                onChange={(on) => setImageryChoice(on ? 'stock' : 'none')}
              />
            </OptionRow>

            {imagery === 'stock' && videoOk && (
              <OptionRow
                label="Video"
                hint="Joins the photography: short loops where motion earns it, plus a full-bleed video slide."
                sub
              >
                <Toggle checked={video} onChange={setVideo} label="Video" />
              </OptionRow>
            )}

            <OptionRow
              label="Section intros"
              hint={
                sectionIntros
                  ? 'A numbered divider slide opens each section.'
                  : 'No dividers: each section opens straight onto its first slide.'
              }
            >
              <Toggle checked={sectionIntros} onChange={setSectionIntros} label="Section intros" />
            </OptionRow>

            {engine === 'codex' && (
              <OptionRow
                label="Refine the deck"
                hint={
                  buildQuality === 'refined'
                    ? 'Reviews every slide and improves only what needs it. Good slides stay untouched.'
                    : 'Quick draft: skips refinement. Layout checks still run.'
                }
              >
                <Toggle
                  checked={buildQuality === 'refined'}
                  onChange={(on) => setBuildQuality(on ? 'refined' : 'quick')}
                  label="Refine the deck"
                />
              </OptionRow>
            )}

            {/* Build engine. The guard is a safety net rather than a real
                  branch these days: each engine block always returns a block,
                  so `altEngines` is never empty and the row always renders. An
                  engine that can't start stays visible and disabled carrying the
                  server's own reason, so the choice never fails on click, and
                  the reason is somewhere it can actually be read. */}
            {altEngines.length > 0 && (
              <OptionRow
                label="Build engine"
                stack={engineRowWide}
                hint={
                  engine === 'codex'
                    ? `Codex builds the deck on ${engineBlocks.codex?.model || 'GPT'} using the server’s ChatGPT subscription.`
                    : engine === 'fast'
                      ? `${engineBlocks.fast?.model} builds the deck on OpenCode. Same brief, same playbook, a cheaper model, for comparing the engines. Always charged to the gateway key.`
                      : engine === 'pi'
                        ? // The provider is a SETTING on this engine, so the hint
                          // names it: "Pi builds the deck" says nothing about
                          // which model or account is about to be paid.
                          `Pi builds the deck on ${engineBlocks.pi?.model || 'the model Settings names'}${engineBlocks.pi?.auth === 'subscription' ? ` on the ${engineBlocks.pi?.subscriptionProvider || 'subscription'} login signed in on this server` : engineBlocks.pi?.baseUrl ? ` at ${engineBlocks.pi.baseUrl}, metered` : ', metered'}. Same brief, same playbook.`
                        : `Claude builds the deck. The default, and what deck quality is tuned on. ${engineBlocks.claude?.auth === 'api' ? 'Charged to the Anthropic API key, at API rates.' : 'No cost per build.'}${blockedEngines.map((e) => ` ${e.issue}`).join('')}`
                }
              >
                <Segmented
                  value={engine}
                  onChange={(v) => {
                    touched.current.engine = true;
                    setEngine(v);
                  }}
                  label="Build engine"
                  wrap={engineRowWide}
                  options={[
                    // Built from the server block like the others, NOT
                    // hardcoded: Claude can now be unavailable for a
                    // credential reason of its own (set to an API key whose
                    // key was removed), and a choice must never fail on click.
                    {
                      value: 'claude',
                      label: 'Claude',
                      disabled: engineBlocks.claude ? !engineBlocks.claude.available : false,
                      title: engineBlocks.claude?.issue || undefined,
                    },
                    ...altEngines.map((e) => ({
                      value: e.value,
                      label: e.label,
                      disabled: !e.available,
                      title: e.issue || undefined,
                    })),
                  ]}
                />
              </OptionRow>
            )}

            {/* Which saved Pi setup this ONE build runs on. Pi's whole point
                  is that the provider is configuration, and the ⚙ drawer made
                  switching a click — but a click in the drawer changes the
                  server for everyone until someone changes it back, which is
                  the wrong shape for "build this deck on the other provider and
                  see". So it is answered here, per build, and Settings is left
                  alone.

                  A `sub` row: it says nothing until the Build engine row above
                  it says Pi. Hidden entirely with nothing saved — an empty
                  select would advertise a feature whose only answer is the
                  default. A `select`, not a `Segmented`, because the options
                  are free-text labels someone typed in the drawer and a pill
                  track cannot hold "OpenCode Go · deepseek-v4-flash". */}
            {engine === 'pi' && piProfiles.length > 0 && (
              <OptionRow label="Provider" sub stack hint={piProfileHint(chosenPiProfile)}>
                <select
                  value={chosenPiProfile ? chosenPiProfile.label : ''}
                  onChange={(e) => setPiProfile(e.target.value)}
                  aria-label="Pi provider for this build"
                >
                  {/* A REAL option, not a blank: "use the ⚙ setting" is the
                        default answer and has to be selectable again after
                        picking a setup. */}
                  <option value="">Use the current settings</option>
                  {piProfiles.map((p, i) => (
                    <option key={`${p.label}-${i}`} value={p.label}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </OptionRow>
            )}

            {/* Colour: the same accent picker the editor uses (tokens only —
                  the intake validates baseColour against the brand roster, so a
                  custom hex would be refused server-side). The two treatments
                  that IGNORE the picked colour disable it rather than hiding it,
                  so the row never silently means nothing. */}
            <OptionRow
              label="Colour"
              hint={
                colourInert
                  ? sourceDeck && colourMatch
                    ? 'Taken from your uploaded deck.'
                    : 'Chosen per section by the agent.'
                  : 'One accent across the deck. Change it any time in the editor.'
              }
            >
              <AccentSwatches
                value={colourInert ? null : baseColour}
                allowCustom={false}
                disabled={colourInert}
                onPick={(token) => {
                  setColourMatch(false);
                  setColourTreatment('single');
                  setBaseColour(token);
                }}
              />
            </OptionRow>

            {sourceDeck && (
              <OptionRow label="Match my deck’s colours" hint="Maps the accents it already uses onto the palette." sub>
                <Toggle checked={colourMatch} onChange={setColourMatch} label="Match my deck’s colours" />
              </OptionRow>
            )}

            <OptionRow label="A colour per section" hint="Each section takes a different accent." sub>
              <Toggle
                checked={colourTreatment === 'sections'}
                onChange={(on) => {
                  setColourMatch(false);
                  setColourTreatment(on ? 'sections' : 'single');
                }}
                label="A colour per section"
              />
            </OptionRow>

            {/* HOW to build it, kept away from WHAT to say. The notes box
                  above rides along with the brief as another source document,
                  so a line like "give every platform the same structure" lands
                  as content the builder might set in type rather than an
                  instruction it must follow. This one reaches the launch prompt
                  as a directive instead (server: `directionsDirective`).
                  Folded away by default — most builds want none. */}
            <OptionRow
              label="Extra instructions"
              hint={
                directionsOn
                  ? 'Followed as instructions. They never appear as words on a slide.'
                  : 'Optional. Say how the deck should be built: structure, order, what to compare.'
              }
            >
              <Toggle checked={directionsOn} onChange={setDirectionsOn} label="Extra instructions" />
            </OptionRow>

            {directionsOn && (
              <OptionRow
                label="How it should be built"
                hint="A few sentences. Brand, layout and geometry rules still stand."
                sub
                stack
              >
                <textarea
                  className="new-deck__notes"
                  rows={3}
                  value={directions}
                  maxLength={MAX_DIRECTIONS}
                  placeholder="e.g. Give every platform the same slide structure, in the same order, so they can be compared."
                  onChange={(e) => setDirections(e.target.value)}
                />
              </OptionRow>
            )}
          </OptionRows>
        </section>
      </div>

      <footer className="modal__footer new-deck__foot">
        <span className="muted small new-deck__foot-hint">
          {building ? (
            // role=status so a screen reader hears it without stealing focus;
            // the clock lives outside it so the whole line isn't re-announced
            // every second.
            <span className="new-deck__starting">
              <span className="new-deck__spinner new-deck__spinner--inline" aria-hidden="true" />
              <span role="status">{startingNote}</span>
              <span className="new-deck__starting-clock" aria-hidden="true">
                {Math.floor(startSecs / 60)}:{String(startSecs % 60).padStart(2, '0')}
              </span>
            </span>
          ) : startError ? (
            <strong className="error">{startError}</strong>
          ) : (
            footHint
          )}
        </span>
        <button type="button" className="btn btn--ghost" onClick={onCancel} disabled={building}>
          Cancel
        </button>
        <button type="button" className="btn btn--primary" onClick={submit} disabled={!canBuild}>
          {building ? (
            <>
              <span className="new-deck__spinner new-deck__spinner--btn" aria-hidden="true" />
              Starting…
            </>
          ) : (
            'Build deck'
          )}
        </button>
      </footer>
    </Modal>
  );
}

/** Ceiling on the "Extra instructions" box, matched by the intake validator.
 *  Long enough for a paragraph of direction, short enough that nobody pastes a
 *  second brief in here — a brief belongs in the upload, where it is read as
 *  content and covered. */
const MAX_DIRECTIONS = 1200;

const BUDGET_HINTS = {
  short: 'About 10–15 slides.',
  standard: 'About 20–35 slides.',
  deep: 'About 40–60 slides.',
};

/** Dedupe File arrays by name+size (re-picking the same file is a no-op). */
function dedupe(files: File[]): File[] {
  const seen = new Set<string>();
  return files.filter((f) => {
    const k = f.name + ':' + f.size;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}
